<template>
  <div class="map-container">
    <div id="map"></div>
    <div class="control-panel" :class="{ expanded: controlsExpanded }">
      <button @click="controlsExpanded = !controlsExpanded">
        <span class="material-symbols-outlined">menu</span>
      </button>
      <div v-if="controlsExpanded">
        <div class="d-flex">
          <select v-model="ausgewahelteStreckeName" @change="drawRoute">
            <option
              v-for="(coords, name) in strecken"
              :key="name"
              :value="name"
            >
              {{ name }}
            </option>
          </select>
        </div>
        <div class="d-flex">
          <select v-model="selectedStyle" @change="setStyle">
            <option
              v-for="(style, styleIndex) of moeglicheStyles"
              :key="styleIndex"
              :value="style"
            >
              {{ style.name }}
            </option>
          </select>
        </div>
        <div class="d-flex">
          <input
            type="number"
            v-model.number="speed"
            min="1"
            max="50"
            step="1"
          />
          km/h
        </div>

        <div>
          <label>
            <input
              type="radio"
              value="Kein Wetter"
              v-model="weather"
              @change="setWeather"
            />
            Kein Wetter
          </label>

          <label>
            <input
              type="radio"
              value="Regen"
              v-model="weather"
              @change="setWeather"
            />
            Regen
          </label>

          <label>
            <input
              type="radio"
              value="Schnee"
              v-model="weather"
              @change="setWeather"
            />
            Schnee
          </label>
        </div>
        <button @click="toggleStartPause">
          {{ isPlaying ? "Pause" : "Start" }}
        </button>
        <button @click="stop">Stop</button>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import * as turf from "@turf/turf";
import { notify } from "@kyvg/vue3-notification";

export default {
  data() {
    return {
      selectedStyle: {
        name: "Standard",
        url: "mapbox://styles/mapbox/standard",
      },
      moeglicheStyles: [
        { name: "Standard", url: "mapbox://styles/mapbox/standard" },
        { name: "Straßen", url: "mapbox://styles/mapbox/streets-v12" },
        {
          name: "Satellit",
          url: "mapbox://styles/mapbox/satellite-streets-v11",
        },
      ],
      weather: "Kein Wetter",
      map: null,
      routeLayer: null,
      marker: null,
      ausgewahelteStreckeName: "Bootshaus <---> Schleuse Kukelorum",
      isPlaying: false,
      currentIndex: 0,
      interval: null,
      controlsExpanded: true,
      speed: 10,
      strecken: {
        "Bootshaus <---> Schleuse Kukelorum": [
          [7.472624, 53.4639],
          [7.472093, 53.463907],
          [7.471596, 53.46315],
          [7.471029, 53.462725],
          [7.469883, 53.461903],
          [7.465342, 53.459781],
          [7.460695, 53.456529],
          [7.4522, 53.450688],
        ],
        "Bootshaus <---> Biotop": [
          [7.472624, 53.4639],
          [7.47212, 53.464064],
          [7.472656, 53.464829],
          [7.473331, 53.464934],
          [7.474018, 53.464721],
          [7.474455, 53.464296],
          [7.475544, 53.4629],
          [7.476228, 53.462008],
          [7.476929, 53.461507],
          [7.482514, 53.458472],
          [7.486146, 53.457015],
        ],
      },
    };
  },
  watch: {
    ausgewahelteStreckeName() {
      this.drawRoute();
    },
  },
  mounted() {
    notify({
      title: "Hinweis",
      text:
        "Diese Seite hier ist erstmal nur ein Experiment. " +
        "Wenn ich Zeit und Lust habe, arbeite ich weiter dran.",
      type: "error",
    });

    mapboxgl.accessToken =
      "pk.eyJ1IjoiZnBlIiwiYSI6ImNrNHlycGUzYzAzaDAzanAyM2N1dWRweWcifQ.0eeLC3R5QWRQqyNggLC_7A";
    this.map = new mapboxgl.Map({
      container: "map", // container ID
      style: this.selectedStyle.url, // style URL
      projection: "globe",
      center: this.strecken[this.ausgewahelteStreckeName][0], // starting position [lng, lat]
      zoom: 12, // Höherer Zoom für mehr Details
      pitch: 0, // Stärkere Neigung für 3D-Effekt
      // bearing: 0, // Blickrichtung (Rotation)
      light: [
        {
          id: "environment",
          type: "ambient",
          properties: {
            color: "rgba(255.0, 0.0, 0.0, 1.0)",
            intensity: 0.4,
          },
        },
        {
          id: "sun_light",
          type: "directional",
          properties: {
            color: "rgba(255.0, 0.0, 0.0, 1.0)",
            intensity: 0.4,
            direction: [200, 40],
            "cast-shadows": true,
            "shadow-intensity": 0.2,
          },
        },
      ],
    });

    this.map.on("style.load", () => {
      this.map.setConfigProperty("basemap", "lightPreset", "dawn");
    });

    this.map.on("load", () => {
      this.marker = new mapboxgl.Marker()
        .setLngLat(this.strecken[this.ausgewahelteStreckeName][0])
        .addTo(this.map);
      this.drawRoute();
    });
  },
  methods: {
    setStyle() {
      this.map.setStyle(this.selectedStyle.url);
    },
    /* use an expression to transition some properties between zoom levels 11 and 13, preventing visibility when zoomed out */
    zoomBasedReveal(value) {
      return ["interpolate", ["linear"], ["zoom"], 11, 0.0, 20, value];
    },
    setWeather() {
      if (this.weather === "Regen") {
        this.map.setSnow(null);
        this.map.setRain({
          density: this.zoomBasedReveal(0.5),
          intensity: 1.0,
          color: "#a8adbc",
          opacity: 0.7,
          vignette: this.zoomBasedReveal(1.0),
          "vignette-color": "#464646",
          direction: [0, 80],
          "droplet-size": [2.6, 18.2],
          "distortion-strength": 0.7,
          "center-thinning": 0, // Rain to be displayed on the whole screen area
        });
      } else if (this.weather === "Schnee") {
        this.map.setRain(null);
        this.map.setSnow({
          density: this.zoomBasedReveal(0.85),
          intensity: 1.0,
          "center-thinning": 0.1,
          direction: [0, 50],
          opacity: 1.0,
          color: `#ffffff`,
          "flake-size": 0.71,
          vignette: this.zoomBasedReveal(0.3),
          "vignette-color": `#ffffff`,
        });
      } else {
        this.map.setSnow(null);
        this.map.setRain(null);
      }
    },
    drawRoute() {
      if (!this.ausgewahelteStreckeName) return;
      const route = this.strecken[this.ausgewahelteStreckeName];

      if (this.map.getSource("route")) {
        this.map.removeLayer("route");
        this.map.removeSource("route");
      }

      const smoothRoute = turf.bezierSpline(turf.lineString(route));

      this.map.addSource("route", {
        type: "geojson",
        data: smoothRoute,
      });

      this.map.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: {},
        paint: {
          "line-color": "#007AFF",
          "line-width": 4,
        },
      });

      this.marker.setLngLat(route[0]);
    },
    toggleStartPause() {
      if (this.isPlaying) {
        clearInterval(this.interval);
      } else {
        this.startAnimation();
      }

      this.isPlaying = !this.isPlaying;
    },
    calculateBearing(start, end) {
      const deltaY = end[1] - start[1];
      const deltaX = end[0] - start[0];
      return (Math.atan2(deltaX, deltaY) * 180) / Math.PI;
    },
    startAnimation() {
      if (!this.ausgewahelteStreckeName) return;

      const route = turf.bezierSpline(
        turf.lineString(this.strecken[this.ausgewahelteStreckeName])
      ).geometry.coordinates;

      this.map.easeTo({
        pitch: 80,
        zoom: 20,
        bearing: this.calculateBearing(route[0], route[1]),
        duration: 1000,
      });

      this.currentIndex = 0;
      this.isPlaying = true;

      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this; // lokale Referenz auf `this` erstellen

      const animate = () => {
        if (!self.isPlaying || self.currentIndex >= route.length - 1) {
          self.stop();
          return;
        }

        const start = route[self.currentIndex];
        const end = route[self.currentIndex + 1];

        let progress = 0;
        const step = (timestamp) => {
          if (!self.lastTimestamp) self.lastTimestamp = timestamp;
          const deltaTime = timestamp - self.lastTimestamp;
          progress += deltaTime / ((3600 / self.speed) * 1000);
          self.lastTimestamp = timestamp;

          if (progress >= 1) {
            self.currentIndex++;
            self.marker.setLngLat(end);

            // Verhindere unendliche Schleife
            if (self.currentIndex >= route.length - 1) {
              self.stop();
              return;
            }

            requestAnimationFrame(animate);
          } else {
            const interpolated = [
              start[0] + (end[0] - start[0]) * progress,
              start[1] + (end[1] - start[1]) * progress,
            ];
            self.marker.setLngLat(interpolated);
            self.map.easeTo({
              center: interpolated,
              duration: 50,
            });

            requestAnimationFrame(step);
          }
        };

        // Setze Blickrichtung nur beim Start
        if (self.currentIndex === 0) {
          self.map.easeTo({
            bearing: self.calculateBearing(route[0], route[1]),
            duration: 1000,
          });
        }

        // Starte den Animation-Tick
        requestAnimationFrame(step);
      };

      console.log(
        "this.isPlaying before requestAnimationFrame",
        this.isPlaying
      );

      // Starte die Animation
      requestAnimationFrame(animate);
    },
    stop() {
      clearInterval(this.interval);
      this.isPlaying = false;
      this.currentIndex = 0;
      if (this.ausgewahelteStreckeName) {
        this.marker.setLngLat(this.strecken[this.ausgewahelteStreckeName][0]);
      }
    },
  },
};
</script>

<style>
.map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
}
#map {
  width: 100%;
  height: 100%;
}
.control-panel {
  position: absolute;
  top: 70px;
  left: 50px;
  background: rgba(255, 255, 255, 0.9);
  padding: 10px;
  border-radius: 5px;
}
.control-panel button {
  display: block;
  margin: 5px 0;
}
</style>
